import { useState } from "react";
import Chart from "react-apexcharts";
import { fmtBRL, fmtInt } from "@/lib/api";
import { apexBase, useChartTheme, PALETTE } from "@/components/production/pcShared";
import { Panel, Empty, GrowthCell, AbcBadge } from "./biShared";

const DIMS = [["skus", "SKUs"], ["stores", "Lojas"], ["types", "Tipos de Copo"]];
const CLASS_INFO = {
  A: { label: "Classe A", desc: "geram ~80% do faturamento", cls: "border-green-600" },
  B: { label: "Classe B", desc: "próximos ~15% do faturamento", cls: "border-yellow-400" },
  C: { label: "Classe C", desc: "cauda longa (~5% restante)", cls: "border-zinc-300" },
};

const labelOf = (dim, r) => (dim === "skus" ? r.sku : dim === "stores" ? r.store : r.label);
const dimUnit = (dim) => (dim === "skus" ? "SKUs" : dim === "stores" ? "lojas" : "tipos");

function AbcSummaryCards({ summary, dim }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="bi-abc-summary">
      {["A", "B", "C"].map((cls) => {
        const s = summary[cls] || { count: 0, gross: 0, share: 0 };
        const info = CLASS_INFO[cls];
        return (
          <div key={cls} className={`tile p-3 border-l-4 ${info.cls}`} data-testid={`bi-abc-card-${cls}`}>
            <div className="flex items-center gap-2">
              <AbcBadge cls={cls} />
              <span className="overline text-[10px] text-zinc-500">{info.label}</span>
            </div>
            <div className="font-mono text-xl font-bold leading-tight mt-1">
              {fmtInt(s.count)} <span className="text-xs font-normal text-zinc-500">{dimUnit(dim)}</span>
            </div>
            <div className="text-[11px] text-zinc-500">{fmtBRL(s.gross)} · {s.share}% do faturamento</div>
            <div className="text-[10px] text-zinc-400">{info.desc}</div>
          </div>
        );
      })}
    </div>
  );
}

function ParetoChart({ chartRows, dim, base }) {
  if (chartRows.length === 0) return <Empty />;
  return (
    <Chart type="line" height={320}
      series={[
        { name: "Faturamento", type: "column", data: chartRows.map((r) => r.gross) },
        { name: "% Acumulado", type: "line", data: chartRows.map((r) => r.cum_share) },
      ]}
      options={{
        ...base,
        chart: { ...base.chart, type: "line", zoom: { enabled: false } },
        stroke: { width: [0, 3], curve: "straight" },
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 2 } },
        markers: { size: [0, 3] },
        xaxis: { categories: chartRows.map((r) => labelOf(dim, r)), labels: { rotate: -45, style: { fontSize: "9px" } } },
        yaxis: [
          { seriesName: "Faturamento", title: { text: "R$" }, labels: { formatter: (v) => fmtBRL(v) } },
          { seriesName: "% Acumulado", opposite: true, min: 0, max: 100, title: { text: "% acumulado" }, labels: { formatter: (v) => `${Math.round(v)}%` } },
        ],
        annotations: {
          yaxis: [{ y: 80, yAxisIndex: 1, borderColor: "#FF5E5E", strokeDashArray: 4,
            label: { text: "80%", style: { color: "#fff", background: "#FF5E5E", fontSize: "9px" } } }],
        },
        colors: [PALETTE[0], PALETTE[3]],
      }} />
  );
}

function AbcTable({ rows, dim }) {
  if (rows.length === 0) return <Empty />;
  return (
    <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
      <table className="table table-compact">
        <thead>
          <tr>
            <th>#</th><th>Classe</th><th>{dim === "skus" ? "SKU" : dim === "stores" ? "Loja" : "Tipo"}</th>
            {dim === "skus" && <th>Produto</th>}
            <th className="text-right">Pedidos</th>
            <th className="text-right">Bruto</th>
            <th className="text-right">Share</th>
            <th className="text-right">Share Acum.</th>
            <th className="text-right">Cresc.</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={labelOf(dim, r)} data-testid={`bi-abc-row-${labelOf(dim, r)}`}>
              <td className="font-mono text-zinc-400">{i + 1}</td>
              <td><AbcBadge cls={r.abc_class} /></td>
              <td className="font-mono font-bold">{labelOf(dim, r)}</td>
              {dim === "skus" && <td className="max-w-[280px] truncate" title={r.name}>{r.name}</td>}
              <td className="text-right font-mono">{fmtInt(r.orders)}</td>
              <td className="text-right font-mono font-bold text-green-600">{fmtBRL(r.gross)}</td>
              <td className="text-right font-mono">{r.share}%</td>
              <td className="text-right font-mono">{r.cum_share}%</td>
              <td className="text-right"><GrowthCell pct={r.growth_pct} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BiAbcTab({ data }) {
  const dark = useChartTheme();
  const base = apexBase(dark);
  const [dim, setDim] = useState("skus");
  const abc = data.abc || {};
  const rows = abc[dim] || [];
  const summary = abc.summary?.[dim] || {};
  const chartRows = rows.slice(0, 30);

  const dimToggle = (
    <div className="flex gap-1">
      {DIMS.map(([k, l]) => (
        <button key={k} onClick={() => setDim(k)}
          className={`text-[10px] px-2 py-0.5 border rounded ${dim === k ? "bg-black text-white border-black" : "border-zinc-300"}`}
          data-testid={`bi-abc-dim-${k}`}>{l}</button>
      ))}
    </div>
  );

  return (
    <div className="space-y-3" data-testid="bi-abc-tab">
      <AbcSummaryCards summary={summary} dim={dim} />
      <Panel title={`GRÁFICO DE PARETO — TOP ${chartRows.length}`} testid="bi-abc-pareto" right={dimToggle}
        note="Barras = faturamento bruto de cada item · Linha = participação acumulada (%). A linha cruzando 80% delimita a Classe A.">
        <ParetoChart chartRows={chartRows} dim={dim} base={base} />
      </Panel>
      <Panel title={`TABELA ABC — ${DIMS.find(([k]) => k === dim)?.[1].toUpperCase()}`} testid="bi-abc-table">
        <AbcTable rows={rows} dim={dim} />
      </Panel>
    </div>
  );
}
